import React, { useEffect, useCallback } from 'react';
import { Stack } from 'react-bootstrap';
import { useGetPostQuery } from 'services';
import useFeedsFollowingStore, {
	setFollowingFeeds,
	setFollowingFeedsMeta,
	setFollowingLastcursor,
	setFollowingLastScrolling,
	setFollowingCurrentCursor,
} from 'store/feedsFollowing';
import { dataLimits } from 'constants/index';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SocialPostSkeletonList } from 'components/Common/Skeleton/SocialPostSkeleton';
import AdvertisementPost from 'components/SocialPost/AdvertisementPost';
import SocialPost from 'components/SocialPost';
import useAuthStore from 'store/auth';
import useTranslate from 'hooks/useTranslate';

const FollowingFeedList: React.FC = () => {
	const { translate } = useTranslate();

	const {
		feeds: feedsList = [],
		feedsMeta,
		lastScrolling,
		lastcursor,
		currentCusror,
	}: Record<string, any> = useFeedsFollowingStore((state) => state);

	const { token: isAuthenticated }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);

	// Following uses connectionType=connected (no allUsers param)
	const searchString = `order=DESC&cursor=${currentCusror || 0}&limit=${dataLimits?.feedsListing}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected`;

	const {
		data: { docs: postList = {}, meta = {} } = {},
		status,
		isStale,
		refetch,
	} = useGetPostQuery({
		searchString: searchString,
		userToken: isAuthenticated,
		isFetch: lastcursor === currentCusror ? false : true,
	});

	useEffect(() => {
		if (typeof refetch === 'function') {
			refetch();
		}
	}, [isAuthenticated]);

	const loadMoreFunc = useCallback(() => {
		setFollowingLastcursor(0);
	}, [feedsMeta]);

	const handleScroll = useCallback((event: any) => {
		const scrollTop = event.target.scrollTop;
		setFollowingLastScrolling(scrollTop);
	}, []);

	useEffect(() => {
		if (
			status === 'success' &&
			postList &&
			Array.isArray(postList.results) &&
			postList.results.length > 0 &&
			isStale
		) {
			setFollowingFeeds([...feedsList, ...postList.results]);
			setFollowingFeedsMeta(meta);
			setFollowingCurrentCursor(meta?.nextCursor || 0);
			setFollowingLastcursor(meta?.nextCursor || 0);
		}
	}, [postList, status]);

	useEffect(() => {
		const scrollableDiv = document.getElementById('infiniteDiv-following-feeds');
		if (scrollableDiv) {
			scrollableDiv.scrollTop = lastScrolling;
		}
	}, [lastScrolling, feedsList.length]);

	const refreshFeeds = useCallback(() => {
		refetch().then((res: any) => {
			if (res?.isSuccess) {
				setFollowingFeeds(res?.data?.docs?.results);
				setFollowingFeedsMeta(res?.data?.meta);
				setFollowingCurrentCursor(res?.data?.meta?.nextCursor || 0);
				setFollowingLastcursor(res?.data?.meta?.nextCursor || 0);
			}
		});
	}, [refetch]);

	return (
		<>
			{feedsList?.length > 0 ? (
				<InfiniteScroll
					dataLength={feedsList.length}
					next={loadMoreFunc}
					hasMore={feedsMeta?.hasMore}
					loader={feedsList.length >= 1 && <SocialPostSkeletonList count={3} noButton={true} />}
					scrollableTarget="infiniteDiv-following-feeds right-area"
					scrollThreshold="300px"
					refreshFunction={loadMoreFunc}
					pullDownToRefreshThreshold={50}
					onScroll={handleScroll}
					pullDownToRefreshContent={
						<h3 style={{ textAlign: 'center' }}>
							&#8595; {translate('Common.pullDownToRefresh')}
						</h3>
					}
					releaseToRefreshContent={
						<h3 style={{ textAlign: 'center' }}>
							&#8593; {translate('Common.releaseToRefresh')}
						</h3>
					}
				>
					{feedsList.length > 0 ? (
						feedsList.map((post: any, index: number) =>
							post?.isAdvertisement ? (
								<AdvertisementPost
									post={post}
									key={`following-${post?.postId}-${index}-${post?.userId}`}
								/>
							) : (
								<SocialPost
									post={post}
									key={`following-${post?.postId}-${index}-${post?.userId}`}
									refetch={refreshFeeds}
								/>
							),
						)
					) : (
						<Stack className="fw-bold">{translate('Common.noPostFound')}</Stack>
					)}
				</InfiniteScroll>
			) : (
				<>
					{status === 'pending' ? (
						<SocialPostSkeletonList count={3} noButton={true} />
					) : (
						<Stack className="fw-bold">{translate('Common.noPostFound')}</Stack>
					)}
				</>
			)}
		</>
	);
};

export default FollowingFeedList;
